import { Accent, ProgressRing, Section, displayTitle } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/** Four weeks of a client's plan, day by day: done, missed or a rest day. */
const MONTH: ("done" | "missed" | "rest")[][] = [
  ["done", "rest", "done", "rest", "done", "rest", "rest"],
  ["done", "rest", "missed", "rest", "done", "done", "rest"],
  ["done", "rest", "done", "rest", "done", "rest", "rest"],
  ["done", "rest", "done", "rest", "missed", "done", "rest"],
];
const DAY_TONE = {
  done: "bg-primary-foreground",
  missed: "bg-primary-foreground/25 ring-1 ring-inset ring-primary-foreground/40",
  rest: "bg-primary-foreground/10",
};

/** Before and after for one sample client, over twelve weeks. */
const MEASUREMENTS = [
  { id: "weight", name: () => m.platform_view_weight(), from: 84.2, to: 80.6, unit: "kg" },
  { id: "waist", name: () => m.platform_view_waist(), from: 92, to: 86, unit: "cm" },
  { id: "squat", name: () => m.platform_plan_squat(), from: 67.5, to: 80, unit: "kg" },
];

/** One day's protein, carbs and fat against the target, in grams. */
const MACROS = [
  { id: "protein", name: () => m.platform_macro_protein(), grams: 148, goal: 160 },
  { id: "carbs", name: () => m.platform_macro_carbs(), grams: 190, goal: 260 },
  { id: "fat", name: () => m.platform_macro_fat(), grams: 52, goal: 70 },
];

const number = (value: number) => value.toLocaleString(getLocale(), { maximumFractionDigits: 1 });

const card = "flex min-h-[440px] flex-col rounded-[28px] border border-border bg-card p-7";
const cardTitle = "text-3xl leading-[1.05] font-medium tracking-[-0.035em]";
const cardBody = "mt-3.5 text-[15px] leading-normal";

/** What the client sees of what the trainer set up: the plan done, the numbers moving, the day's food. */
export const ClientView = () => (
  <Section>
    <h2 className={cn(displayTitle, "mb-12 px-1 text-center")}>
      {m.platform_view_title_lead()} <Accent>{m.platform_view_title_accent()}</Accent>
    </h2>
    <div className="grid gap-3 lg:grid-cols-3">
      <article className={cn(card, "border-transparent bg-primary text-primary-foreground")}>
        <h3 className={cardTitle}>{m.platform_view_plan_title()}</h3>
        <p className={cn(cardBody, "opacity-75")}>{m.platform_view_plan_body()}</p>
        <div aria-hidden className="mt-auto pt-7">
          <div className="grid gap-1.5">
            {MONTH.map((week, row) => (
              <div key={`week-${row + 1}`} className="grid grid-cols-7 gap-1.5">
                {week.map((day, column) => (
                  <i key={`${row}-${column}`} className={cn("aspect-square rounded-lg", DAY_TONE[day])} />
                ))}
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm font-semibold">{m.platform_view_plan_done({ share: 86 })}</p>
        </div>
      </article>

      <article className={card}>
        <h3 className={cardTitle}>{m.platform_view_numbers_title()}</h3>
        <p className={cn(cardBody, "text-muted-foreground")}>{m.platform_view_numbers_body()}</p>
        <div aria-hidden className="mt-auto grid gap-2 pt-7">
          {MEASUREMENTS.map((row) => {
            const change = row.to - row.from;
            return (
              <div
                key={row.id}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-2xl bg-[var(--site-well)] px-4 py-3"
              >
                <span className="truncate text-sm font-medium">{row.name()}</span>
                <span className="text-sm text-muted-foreground tabular-nums">
                  {number(row.from)} → <b className="font-semibold text-foreground">{number(row.to)}</b>
                </span>
                <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground tabular-nums">
                  {change > 0 ? "+" : "−"}
                  {number(Math.abs(change))} {row.unit}
                </span>
              </div>
            );
          })}
        </div>
      </article>

      <article className={card}>
        <h3 className={cardTitle}>{m.platform_view_diet_title()}</h3>
        <p className={cn(cardBody, "text-muted-foreground")}>{m.platform_view_diet_body()}</p>
        <div aria-hidden className="mt-auto pt-7">
          <div className="flex items-center gap-4">
            <ProgressRing share={1840 / 2400} size={84} stroke={11} />
            <div>
              <b className="block text-2xl font-medium tracking-[-0.04em] tabular-nums">{number(1840)}</b>
              <small className="text-sm text-muted-foreground">{m.platform_view_diet_of({ goal: number(2400) })}</small>
            </div>
          </div>
          <div className="mt-5 grid gap-3">
            {MACROS.map((macro) => (
              <div key={macro.id} className="grid gap-1.5">
                <div className="flex justify-between text-[13px]">
                  {macro.name()}
                  <span className="text-muted-foreground tabular-nums">
                    {macro.grams} / {macro.goal} g
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[var(--site-well)]">
                  <i
                    className="block h-full rounded-full bg-primary"
                    style={{ width: `${(macro.grams / macro.goal) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </article>
    </div>
  </Section>
);
